import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

import React, { useRef, useEffect, useContext, useCallback, useState } from 'react';
import { useId } from '@reach/auto-id';
import styled from 'styled-components';
import { PopoverContent } from "../Popover";
import { PopoverContext } from "../Popover/PopoverContext";
import { useDescendant } from "../../hooks/useDescendants";
import { noop } from "../../utils/noop";
import { MenuListContext } from "./MenuListContext";
import { useMovingTowardSubmenu } from "./useMovingTowardSubmenu";
import { MenuList } from "./MenuList";
import { theme } from "../../theme";
import { wrapEvent } from "../../utils/wrap-event";
/**
 * Determine of the offsets for positioning submenus
 * depending upon if they appear positioned at the start
 * or end of its content.
 */

var defaultSubmenuOffset = function defaultSubmenuOffset(_ref) {
  var placement = _ref.placement;

  if (placement.includes('end')) {
    return [8, 8];
  }

  return [-8, 8];
};

var HorizontalPadding = styled.div.withConfig({
  displayName: "MenuItem__HorizontalPadding",
  componentId: "vapor__sc-1sjxgiq-0"
})(["padding:0 ", ";width:100%;"], theme.spaces[8]);
export function MenuItem(_ref2) {
  var children = _ref2.children,
      _ref2$onSelect = _ref2.onSelect,
      onSelect = _ref2$onSelect === void 0 ? noop : _ref2$onSelect,
      submenu = _ref2.submenu,
      _ref2$shouldCloseMenu = _ref2.shouldCloseMenuOnSelect,
      shouldCloseMenuOnSelect = _ref2$shouldCloseMenu === void 0 ? true : _ref2$shouldCloseMenu,
      _ref2$submenuHoverDel = _ref2.submenuHoverDelay,
      submenuHoverDelay = _ref2$submenuHoverDel === void 0 ? 75 : _ref2$submenuHoverDel,
      _ref2$delayBeforeClos = _ref2.delayBeforeClosingMenuUponSelection,
      delayBeforeClosingMenuUponSelection = _ref2$delayBeforeClos === void 0 ? 0 : _ref2$delayBeforeClos,
      onClickProp = _ref2.onClick,
      isLink = _ref2.isLink;
  var id = useId();
  var labelRef = useRef(null);
  var containerRef = useRef(null);
  var isHoveringRef = useRef(false);
  var popoverRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      shouldExpand = _useState2[0],
      setShouldExpand = _useState2[1];

  var shouldExpandRefTimer = useRef();
  var delayBeforeClosingTimer = useRef();

  var _useContext = useContext(MenuListContext),
      selectedId = _useContext.selectedId,
      expandedId = _useContext.expandedId,
      dispatch = _useContext.dispatch,
      mouse = _useContext.mouse;

  var _useMovingTowardSubme = useMovingTowardSubmenu({
    id: id,
    popoverRef: popoverRef,
    enabled: !!submenu
  }),
      isMovingTowardSubmenu = _useMovingTowardSubme.isMovingTowardSubmenu,
      preventSelection = _useMovingTowardSubme.preventSelection,
      movementEventHandlers = _objectWithoutProperties(_useMovingTowardSubme, ["isMovingTowardSubmenu", "preventSelection"]);

  var _useContext2 = useContext(PopoverContext),
      onRequestClose = _useContext2.onRequestClose; // Register this MenuItem with MenuList


  useDescendant(id, labelRef, {
    containerRef: containerRef
  });
  var isExpanded = !!submenu && expandedId === id;
  var isSelected = selectedId === id;
  /**
   * Focus our menu item when it becomes selected.
   */

  useEffect(function () {
    // Only actually focus the element if navigating
    // via keyboard.
    if (isSelected && !mouse) {
      labelRef.current.focus();
    }
  }, [isSelected, mouse]);
  /**
   * Clear potential timers on unmount
   */

  useEffect(function () {
    return function () {
      clearTimeout(delayBeforeClosingTimer.current);
      clearTimeout(shouldExpandRefTimer.current);
    };
  }, []);
  /**
   * Handle delayed closure of the Popover
   */

  var onClose = useCallback(function () {
    // prevent multiple clicks from firing
    if (delayBeforeClosingTimer.current) {
      return;
    }

    delayBeforeClosingTimer.current = window.setTimeout(function () {
      onRequestClose();
    }, delayBeforeClosingMenuUponSelection);
  }, [delayBeforeClosingMenuUponSelection, onRequestClose]);
  /**
   * When clicked, trigger `onSelect` and possibly
   * close our entire menu.
   */

  var onClick = useCallback(function (e) {
    if (isExpanded) {
      return;
    }

    onSelect();

    if (onClickProp) {
      onClickProp(e);
    }

    if (shouldCloseMenuOnSelect) {
      onClose();
    }
  }, [isExpanded, onClickProp, onClose, onSelect, shouldCloseMenuOnSelect]);
  /**
   * When entering, select and potentially expand our
   * submenu.
   */

  var onMouseEnter = useCallback(function () {
    isHoveringRef.current = true; // Only expand a submenu if the user has hovered
    // for 75ms.

    if (submenu) {
      shouldExpandRefTimer.current = window.setTimeout(function () {
        setShouldExpand(true);
      }, isExpanded ? 0 : submenuHoverDelay);
    }

    if (!preventSelection) {
      dispatch({
        type: isExpanded ? 'SELECT' : 'SELECT_AND_COLLAPSE',
        payload: {
          id: id,
          mouse: true
        }
      });
    }
  }, [submenu, submenuHoverDelay, isExpanded, dispatch, id, preventSelection]);
  /**
   * Potentially expand a menu
   */

  var onMouseMove = useCallback(function () {
    if (!preventSelection && shouldExpand && !isExpanded && !!submenu) {
      dispatch({
        type: 'SELECT_AND_EXPAND',
        payload: {
          id: id,
          mouse: true
        }
      });
    }
  }, [dispatch, id, shouldExpand, isExpanded, preventSelection, submenu]);
  /**
   * When exiting, possibly collapse our submenu and
   * deselect the current menu item.
   */

  var onMouseLeave = useCallback(function () {
    isHoveringRef.current = false;
    setShouldExpand(false);

    if (shouldExpandRefTimer.current) {
      clearTimeout(shouldExpandRefTimer.current);
    }

    if (isExpanded && !isMovingTowardSubmenu.current) {
      dispatch({
        type: 'COLLAPSE_AND_DESELECT',
        mouse: true
      });
    } else {
      dispatch({
        type: 'DESELECT',
        mouse: true
      });
    }
  }, [dispatch, isExpanded, isMovingTowardSubmenu]);
  /**
   * If our preventSelection state changes to false while we are
   * hovering this menu item, select it and either expand its
   * submenu or collapse the already opened menu.
   */

  useEffect(function () {
    if (!preventSelection && isHoveringRef.current) {
      dispatch({
        type: shouldExpand ? 'SELECT_AND_EXPAND' : 'SELECT_AND_COLLAPSE',
        payload: {
          id: id,
          mouse: true
        }
      });
    }
  }, [shouldExpand, dispatch, id, preventSelection]);
  /**
   * Handle key events related to the expansion
   * of a potential submenu.
   */

  var onKeyDown = useCallback(function (e) {
    function expand() {
      if (submenu) {
        dispatch({
          type: 'EXPAND_AND_FOCUS_CHILD',
          payload: {
            id: selectedId,
            mouse: false
          }
        });
      }
    }

    switch (e.key) {
      // Expand our submenu
      case 'ArrowRight':
        e.preventDefault();
        e.stopPropagation();
        expand();
        break;
      // Either expand our submenu if we have one,
      // or select the MenuItem.

      case 'Enter':
      case ' ':
        // If menu item is a link don't prevent
        // default behavior.
        if (!isLink) {
          e.preventDefault();
          e.stopPropagation();
        }

        if (submenu) {
          expand();
        } else {
          onSelect();

          if (onClickProp) {
            onClickProp(e);
          }

          if (shouldCloseMenuOnSelect) {
            onClose();
          }
        }

        break;

      default:
        break;
    }
  }, [submenu, dispatch, selectedId, isLink, onSelect, onClickProp, shouldCloseMenuOnSelect, onClose]);
  var menuId = "".concat(id, "-popover-content");
  return /*#__PURE__*/React.createElement(HorizontalPadding, {
    ref: containerRef
  }, children({
    bind: {
      id: id,
      tabIndex: isSelected || isExpanded ? 0 : -1,
      'aria-haspopup': submenu ? 'menu' : undefined,
      'aria-expanded': isExpanded,
      'aria-controls': submenu ? menuId : undefined,
      'data-selected': isSelected,
      ref: labelRef,
      onKeyDown: onKeyDown,
      onClick: onClick,
      onMouseEnter: wrapEvent(onMouseEnter, movementEventHandlers.onMouseEnter),
      onMouseLeave: wrapEvent(movementEventHandlers.onMouseLeave, onMouseLeave),
      onMouseMove: wrapEvent(onMouseMove, movementEventHandlers.onMouseMove)
    },
    isSelected: isSelected,
    isExpanded: isExpanded
  }), submenu && /*#__PURE__*/React.createElement(PopoverContent, {
    id: menuId,
    role: "menu",
    ref: popoverRef,
    offset: defaultSubmenuOffset,
    isShowing: isExpanded,
    placement: "right-start",
    target: labelRef.current,
    shouldUsePositioner: true,
    zIndex: theme.zIndices.popover
  }, submenu.type !== MenuList ? /*#__PURE__*/React.createElement(MenuList, null, submenu) : submenu));
}